import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ISplittingRule } from '../../models/settings.models';

@Component({
  selector: 'app-splitting-rules-list',
  templateUrl: './splitting-rules-list.component.html',
  styleUrls: ['./splitting-rules-list.component.scss'],
})
export class SplittingRulesListComponent {
  @Input() rules: ISplittingRule[] | null = [];
  @Input() loading: boolean | null = false;
  @Output() addRule   = new EventEmitter<void>();
  @Output() edit      = new EventEmitter<ISplittingRule>();
  @Output() delete    = new EventEmitter<number>();
  @Output() toggle    = new EventEmitter<{ id: number; active: boolean }>();

  confirmDeleteId: number | null = null;

  constructor(private translate: TranslateService) {}

  requestDelete(id: number): void {
    this.confirmDeleteId = id;
  }

  confirmDelete(): void {
    if (this.confirmDeleteId !== null) {
      this.delete.emit(this.confirmDeleteId);
      this.confirmDeleteId = null;
    }
  }

  cancelDelete(): void {
    this.confirmDeleteId = null;
  }

  splitSummary(rule: ISplittingRule): string {
    const t = (k: string, p?: object) => this.translate.instant('settings.' + k, p);
    const typeNames: Record<string, string> = {
      brand:                t('splitting_rules_tn_brand'),
      category:             t('splitting_rules_tn_category'),
      product_label:        t('splitting_rules_tn_label'),
      product_custom_field: rule.condition_custom_field_name || t('splitting_rules_custom_field_label'),
    };
    const typeName = typeNames[rule.condition_type] || rule.condition_type;

    if (rule.split_mode === 'specific' && rule.split_values?.length > 0) {
      if (rule.split_values.length === 1) {
        return t('splitting_rules_summary_split_one', { typeName, value: rule.split_values[0] });
      }
      return t('splitting_rules_summary_split_n', { n: rule.split_values.length, typeName });
    }
    if (rule.condition_type === 'product_custom_field' && rule.condition_custom_field_name) {
      return t('splitting_rules_summary_by_custom', { name: rule.condition_custom_field_name });
    }
    return t('splitting_rules_summary_by_type', { typeName });
  }

  applicabilityLabel(rule: ISplittingRule): string {
    const t = (k: string, p?: object) => this.translate.instant('settings.' + k, p);
    if (rule.applicability_type === 'all') return t('splitting_rules_applies_all');
    if (rule.applicability_type === 'customer') {
      const n = rule.applicability_partner_ids?.length || 0;
      return n === 1 ? t('splitting_rules_applies_customer_one', { n }) : t('splitting_rules_applies_customer_many', { n });
    }
    if (rule.applicability_type === 'customer_group') {
      const n = rule.applicability_location_group_ids?.length || 0;
      return n === 1 ? t('splitting_rules_applies_group_one', { n }) : t('splitting_rules_applies_group_many', { n });
    }
    if (rule.applicability_type === 'customer_custom_field') {
      return t('splitting_rules_applies_custom_field', {
        field: rule.applicability_custom_field_name || 'field',
        val: rule.applicability_custom_field_value || '',
      });
    }
    return t('splitting_rules_applies_all');
  }

  onToggle(rule: ISplittingRule): void {
    this.toggle.emit({ id: rule.id, active: !rule.active });
  }
}
